import React, { JSX, useId, useRef, useState } from 'react'
import { CheckIcon } from '@heroicons/react/20/solid'
import { NumberInput } from 'components/NumberInput'
import {
  CELL_LABELS,
  GRID_CELL_COUNT,
  GRID_SIZE,
  MAX_OVERLAY_DIMENSION,
  MIN_OVERLAY_DIMENSION,
  ORIENTATION_LABELS,
  OVERLAY_CANVAS,
  OVERLAY_ORIENTATIONS,
  OverlayOrientation,
  OverlaySize,
  cellsToList,
  chatAreas,
  describeOverlaySize,
  fillerCellsOf,
  gridKeyTarget,
  hasCell,
  withCellToggled,
  withCustom,
  withCustomDimension,
  withOrientation
} from 'lib/overlayGrid'

const LABEL_CLASS = 'mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'
const HELP_CLASS = 'mt-1 text-[11px] text-dm-text-accent'
const INPUT_CLASS = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'

const MAX_FRAME_HEIGHT_PX = 360

export const orientationSizeLabel = (orientation: OverlayOrientation): string => {
  const { width, height } = OVERLAY_CANVAS[orientation]
  return `${width} x ${height}`
}

export const gridFrameStyle = (orientation: OverlayOrientation): React.CSSProperties => {
  const { width, height } = OVERLAY_CANVAS[orientation]
  return {
    aspectRatio: `${width} / ${height}`,
    width: `min(100%, ${(MAX_FRAME_HEIGHT_PX * width) / height}px)`
  }
}

const percentOf = (cells: number): string => `${(cells * 100) / GRID_SIZE}%`

export const FILLER_CELL_STYLE: React.CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0, rgba(255,255,255,0.12) 2px, transparent 2px, transparent 7px)'
}

export interface OverlaySizeGridProps {
  value: OverlaySize
  onChange: (next: OverlaySize) => void
}

export const OverlaySizeGrid = ({ value, onChange }: OverlaySizeGridProps): JSX.Element => {
  const baseId = useId()
  const radioName = `${baseId}-orientation`
  const gridLabelId = `${baseId}-grid-label`
  const gridHelpId = `${baseId}-grid-help`
  const summaryId = `${baseId}-summary`
  const customHelpId = `${baseId}-custom-help`

  const cellRefs = useRef<Array<HTMLButtonElement | null>>([])
  const [focusCell, setFocusCell] = useState<number>(() => {
    const picked = cellsToList(value.cells)
    return picked.length > 0 ? picked[0] : 0
  })

  const paused = value.custom
  const areas = paused ? [] : chatAreas(value.orientation, value.cells)
  const filler = paused ? 0 : fillerCellsOf(value.cells)

  const onCellKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const target = gridKeyTarget(index, e.key)
    if (target == null) return
    e.preventDefault()
    setFocusCell(target)
    cellRefs.current[target]?.focus()
  }

  return (
    <div data-testid='overlay-size-grid'>
      <fieldset disabled={paused} data-testid='overlay-size-orientation'>
        <legend className={LABEL_CLASS}>Orientation</legend>
        <div className='grid grid-cols-2 gap-2'>
          {OVERLAY_ORIENTATIONS.map((o) => {
            const checked = value.orientation === o
            return (
              <label
                key={o}
                className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-brand has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60 ${checked
                  ? 'border-white/20 bg-white/10 font-semibold text-dm-text-primary'
                  : 'border-white/10 font-medium text-dm-text-secondary has-[:enabled]:hover:text-dm-text-primary'}`}
              >
                <input
                  type='radio'
                  name={radioName}
                  value={o}
                  checked={checked}
                  onChange={() => onChange(withOrientation(value, o))}
                  className='sr-only'
                  data-testid={`overlay-size-orientation-${o.toLowerCase()}`}
                />
                <span
                  aria-hidden='true'
                  className={`inline-block shrink-0 rounded-[2px] border-2 border-current ${o === 'DESKTOP' ? 'h-2.5 w-4' : 'h-4 w-2.5'}`}
                />
                <span>{ORIENTATION_LABELS[o]} <span className='whitespace-nowrap'>{orientationSizeLabel(o)}</span></span>
              </label>
            )
          })}
        </div>
      </fieldset>

      <div className='mt-4'>
        <span id={gridLabelId} className={LABEL_CLASS}>Chat area</span>

        <div
          className={`relative mx-auto overflow-hidden rounded-md bg-dm-bg-accent/30 ring-1 ring-white/15 ${paused ? 'opacity-50' : ''}`}
          style={gridFrameStyle(value.orientation)}
          data-testid='overlay-size-frame'
          data-orientation={value.orientation}
        >
          <div
            role='group'
            aria-labelledby={gridLabelId}
            aria-describedby={`${gridHelpId} ${summaryId}`}
            className='absolute inset-0 grid grid-cols-3 grid-rows-3'
          >
            {Array.from({ length: GRID_CELL_COUNT }, (_, i) => {
              const on = hasCell(value.cells, i)
              const covered = !on && hasCell(filler, i)
              return (
                <button
                  key={i}
                  ref={(el) => { cellRefs.current[i] = el }}
                  type='button'
                  aria-pressed={on}
                  aria-label={CELL_LABELS[i]}
                  tabIndex={i === focusCell ? 0 : -1}
                  disabled={paused}
                  onClick={() => {
                    setFocusCell(i)
                    onChange(withCellToggled(value, i))
                  }}
                  onKeyDown={(e) => onCellKeyDown(e, i)}
                  data-testid={`overlay-size-cell-${i}`}
                  data-covered={covered ? 'true' : undefined}
                  style={covered ? FILLER_CELL_STYLE : undefined}
                  className={`flex items-center justify-center transition-colors focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand disabled:cursor-not-allowed ${on
                    ? 'bg-white/25 text-dm-text-primary'
                    : 'enabled:hover:bg-white/5'}`}
                >
                  {on && <CheckIcon aria-hidden='true' className='size-4' />}
                </button>
              )
            })}
          </div>
          <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
            <span className='absolute inset-y-0 left-1/3 w-px bg-white/20' />
            <span className='absolute inset-y-0 left-2/3 w-px bg-white/20' />
            <span className='absolute inset-x-0 top-1/3 h-px bg-white/20' />
            <span className='absolute inset-x-0 top-2/3 h-px bg-white/20' />
          </div>
          {areas.map((a) => (
            <span
              key={a.cells}
              aria-hidden='true'
              className='pointer-events-none absolute rounded-sm ring-2 ring-inset ring-white/70'
              style={{ left: percentOf(a.col), top: percentOf(a.row), width: percentOf(a.cols), height: percentOf(a.rows) }}
              data-testid='overlay-size-area'
            />
          ))}
        </div>
        <p
          id={summaryId}
          aria-live='polite'
          className='mt-2 text-[11px] font-medium text-dm-text-secondary'
          data-testid='overlay-size-summary'
        >
          {describeOverlaySize(value)}
        </p>
        <p id={gridHelpId} className={HELP_CLASS} data-testid='overlay-size-grid-help'>
          {paused
            ? 'Paused while Custom pixel dimensions is on. Turn it off to go back to this orientation and these cells.'
            : 'Click a cell to turn it on or off. Any combination works: one cell, a row, a column, cells that do not touch, or all nine. Cells that touch share one chat, which fills the box around them (striped cells); cells that do not touch each get their own copy. Arrow keys move between cells; Space or Enter turns one on or off.'}
        </p>
      </div>

      <div className='mt-4' data-testid='overlay-size-custom-block'>
        <span className={LABEL_CLASS}>Custom pixel dimensions</span>
        <div className='flex items-center gap-3 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
          <button
            type='button'
            role='switch'
            aria-checked={value.custom}
            aria-label='Custom pixel dimensions'
            aria-describedby={customHelpId}
            onClick={() => onChange(withCustom(value, !value.custom))}
            data-testid='overlay-size-custom-switch'
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand ${value.custom ? 'bg-brand' : 'bg-white/15'}`}
          >
            <span
              aria-hidden='true'
              className={`inline-block size-4 rounded-full bg-white shadow transition-transform ${value.custom ? 'translate-x-[18px]' : 'translate-x-0.5'}`}
            />
          </button>
          <span className='text-sm text-dm-text-primary'>{value.custom ? 'On' : 'Off'}</span>
        </div>
        {value.custom && value.width != null && value.height != null && (
          <div className='mt-3 grid grid-cols-2 gap-3'>
            <label className='block'>
              <span className={LABEL_CLASS}>Width (px)</span>
              <NumberInput
                value={value.width}
                min={MIN_OVERLAY_DIMENSION}
                max={MAX_OVERLAY_DIMENSION}
                onCommit={(n) => onChange(withCustomDimension(value, 'width', n))}
                aria-label='Overlay width in pixels'
                data-testid='overlay-size-width'
                className={INPUT_CLASS}
              />
            </label>
            <label className='block'>
              <span className={LABEL_CLASS}>Height (px)</span>
              <NumberInput
                value={value.height}
                min={MIN_OVERLAY_DIMENSION}
                max={MAX_OVERLAY_DIMENSION}
                onCommit={(n) => onChange(withCustomDimension(value, 'height', n))}
                aria-label='Overlay height in pixels'
                data-testid='overlay-size-height'
                className={INPUT_CLASS}
              />
            </label>
          </div>
        )}
        <p id={customHelpId} className={HELP_CLASS}>
          While this is on, the overlay uses exactly this width and height,
          and the orientation and grid above are paused. Turning it off goes
          back to them and keeps this size for next time. Width and height
          must each be between {MIN_OVERLAY_DIMENSION} and {MAX_OVERLAY_DIMENSION} pixels.
        </p>
      </div>
    </div>
  )
}
